import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/common/PasswordInput';
import { Mountain, Loader2, ShieldCheck, ArrowLeft } from 'lucide-react';

const MIN_LEN = 15; // policy condivisa col backend (unico fattore)

// Pagina PUBBLICA per impostare una nuova password dal link ricevuto via email.
export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const tooShort = pw.length > 0 && pw.length < MIN_LEN;
  const mismatch = pw2.length > 0 && pw !== pw2;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (pw.length < MIN_LEN) { setError(`La password deve avere almeno ${MIN_LEN} caratteri.`); return; }
    if (pw !== pw2) { setError('Le due password non coincidono.'); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: pw });
      setDone(true);
      toast.success('Password aggiornata. Ora puoi accedere.');
      setTimeout(() => navigate('/login'), 1800);
    } catch (err) {
      setError(err.response?.data?.detail || 'Impossibile reimpostare la password. Richiedi un nuovo link.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center gap-2.5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Mountain className="h-5 w-5" /></div>
          <div><p className="font-display text-lg font-bold">Sila Experiences</p><p className="text-xs text-muted-foreground">Nuova password</p></div>
        </div>

        {!token ? (
          <div data-testid="reset-password-no-token">
            <h1 className="font-display text-2xl font-bold tracking-tight">Link non valido</h1>
            <p className="mt-2 text-sm text-muted-foreground">Il link di recupero non è completo. Richiedine uno nuovo dalla pagina di accesso.</p>
            <p className="mt-6 text-sm"><Link to="/recupera-password" className="inline-flex items-center font-medium text-primary hover:underline"><ArrowLeft className="mr-1.5 h-4 w-4" />Richiedi un nuovo link</Link></p>
          </div>
        ) : done ? (
          <div data-testid="reset-password-done">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-accent"><ShieldCheck className="h-6 w-6 text-primary" /></div>
            <h1 className="font-display text-2xl font-bold tracking-tight">Password aggiornata</h1>
            <p className="mt-2 text-sm text-muted-foreground">Reindirizzamento all'accesso in corso…</p>
          </div>
        ) : (
          <>
            <h1 className="font-display text-2xl font-bold tracking-tight">Scegli una nuova password</h1>
            <p className="mt-1 text-sm text-muted-foreground">Usa almeno {MIN_LEN} caratteri. Ti consigliamo una frase facile da ricordare ma difficile da indovinare.</p>
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-password-form">
              <div className="space-y-2"><Label htmlFor="pw">Nuova password</Label><PasswordInput id="pw" autoComplete="new-password" required value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Almeno 15 caratteri" data-testid="reset-password-input" toggleTestId="reset-password-toggle" /></div>
              {tooShort && <p className="text-xs text-muted-foreground">Ancora {MIN_LEN - pw.length} caratteri…</p>}
              <div className="space-y-2"><Label htmlFor="pw2">Ripeti password</Label><PasswordInput id="pw2" autoComplete="new-password" required value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Ripeti la password" data-testid="reset-password-confirm-input" toggleTestId="reset-password-confirm-toggle" /></div>
              {mismatch && <p className="text-xs text-destructive">Le password non coincidono.</p>}
              {error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="reset-password-error">{error}</p>}
              <Button type="submit" className="w-full" disabled={loading} data-testid="reset-password-submit-button">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Imposta la nuova password</Button>
            </form>
            <p className="mt-6 text-sm"><Link to="/login" className="inline-flex items-center font-medium text-primary hover:underline"><ArrowLeft className="mr-1.5 h-4 w-4" />Torna all'accesso</Link></p>
          </>
        )}
      </div>
    </div>
  );
}
